/**
 * 获客舱的枚举 → 中文面。**取值域按契约 §2 / §4.2 / §5,新值不许在前端造** ——
 * 查不到就原样显示那个 code(而不是显示空或"未知"),这样后端加了新源当场看得见。
 */
import type {
  AcqCadence,
  AcqDiscoverFunnel,
  AcqFeedbackCode,
  AcqPrefLevel,
  AcqScores,
  AcqState,
  AcqSuggestedAction,
} from "../api/acqService";

export const STATE_LABEL: Record<AcqState, string> = {
  // 1004 A4:原「刚发现」挨着「35 天前发生」读成自相矛盾 —— 这一格是**跟进阶段**不是时间,
  // 改成说阶段的话;「什么时候发现的」另由 discoveredText 按 detectedAt 写日期。
  DISCOVERED: "待初筛",
  QUALIFIED: "已初筛",
  READY_TO_CONTACT: "可以联系",
  FIRST_TOUCH: "已首次接触",
  ENGAGED: "在谈",
  NURTURE: "长期跟进",
  QUALIFIED_OPPORTUNITY: "明确商机",
  WON: "成了",
  LOST: "没成",
};

/** 终态(契约 §2 TERMINAL)。 */
export const TERMINAL_STATES: readonly AcqState[] = ["WON", "LOST"];

export const STATE_ORDER: readonly AcqState[] = [
  "DISCOVERED",
  "QUALIFIED",
  "READY_TO_CONTACT",
  "FIRST_TOUCH",
  "ENGAGED",
  "NURTURE",
  "QUALIFIED_OPPORTUNITY",
  "WON",
  "LOST",
];

/** 契约 §5 信号源。取值域**开放**,查不到回落到 code 本身。 */
const SOURCE_LABEL: Record<string, string> = {
  zhihu_question: "知乎提问",
  douyin_comment: "抖音评论",
  tender_ccgp: "政府采购公告",
  wechat_mp: "公众号动态",
  amap_poi: "地图新店",
  // 0917 v2(契约 §12.6 `SOURCE_WEIBO_EVENT`)。
  weibo_event: "微博事件",
  // 0917 批次二(契约 §13.2):这几种**这一版起会真的出卡**,所以面要配上。
  // ★ 还没接通的(web_change / linkedin / hiring)**故意仍不预填** ——
  //   它们真的开始出卡的那天,面上会直接露出 type 码,当场就知道少配了一个面。
  wechat_search: "微信搜一搜",
  xhs_note: "小红书笔记",
  gov_sme: "政府公示(专精特新·环评)", // 1102 平台问题 50
  gov_penalty: "行政处罚公告",
  hitech_cert: "高企证书到期",
  kuaishou_comment: "快手评论",
  bilibili_comment: "B 站评论",
  // 1008 TikHub 扩展:西瓜视频评论线,与 B站/快手同形(目录卡上的全名「西瓜视频评论意向」由 api 给)。
  xigua_comment: "西瓜视频评论",
  // 0917 批次三(契约 §14.1):第一方意向 —— 料来自自家客服会话,不打任何外部数据商。
  first_party: "自家客服会话",
  // 1015 企业库 P2:按行业 / 地区 / 标签从企业库筛出来、用户勾选入库的公司(不是信号)。
  ent_search: "企业库",
  // 1025 启客拓客:在拓客工作台(嵌入启客)里解锁、启客回推的企业,落卡即带联系方式。
  qike: "拓客平台",
};

/** 线索库 / 今日商机筛子里的来源取值域 —— 与上表同一份口径,顺序按主力程度。 */
export const FILTERABLE_SOURCES: readonly string[] = [
  // ★ 第一方排第一:它是唯一一条「对方已经在跟你说话」的线,也是唯一免费的那条。
  "first_party",
  "tender_ccgp",
  "zhihu_question",
  "wechat_mp",
  "weibo_event",
  "douyin_comment",
  "amap_poi",
  "ent_search",
  "qike",
  "wechat_search",
  "xhs_note",
  "gov_sme",
  "gov_penalty",
  "hitech_cert",
  "kuaishou_comment",
  "bilibili_comment",
  "xigua_comment",
];

export function sourceLabel(code: string): string {
  return SOURCE_LABEL[code] ?? code;
}

// ── 发现目录的三张面(契约 §12.2 / §12.3) ─────────────────────────────────

/**
 * 推荐档。
 * ★ 说人话不说代号:`P0` 对用户没有意义,「主推」有。
 * ★ `NA` 写「未推断」不写「未知」——「未知」听起来像坏了,「未推断」说的是还没跑过那一步
 *   (KB 为空时**全部**都是 NA,实测 33 个租户绝大多数 KB=0,那是常态不是故障)。
 */
export const LEVEL_LABEL: Record<AcqPrefLevel, string> = {
  P0: "主推",
  P1: "可试",
  P2: "不建议",
  NA: "未推断",
};

/** 排序用:主推在最前,未推断在最后。 */
export const LEVEL_ORDER: readonly AcqPrefLevel[] = ["P0", "P1", "P2", "NA"];

/** ★「每周一」不是「每周」:用户要知道是哪一天,不然他不知道该哪天早上看手机。 */
export const CADENCE_LABEL: Record<AcqCadence, string> = {
  DAILY: "每天",
  WEEKLY: "每周一",
};

/**
 * 原型(archetype)。同一个原型的种类**找法是一样的**,所以它是目录页上最有用的一句分类:
 * 用户看懂了「登记公示」这一类,15 种里的 5 种就一起看懂了。
 * 取值域开放(与 source 同则),查不到原样显示。
 */
const ARCHETYPE_LABEL: Record<string, string> = {
  registry: "登记公示",
  question: "需求提问",
  event: "事件",
  comment: "评论区",
  local: "本地实体",
  entity: "主体动态",
  trend: "趋势",
  // 0917 批次三(契约 §14.1):它自成一个原型 —— 别的原型都在外面找人,
  // 这一个是在**自家会话里**认出已经来问的人。
  // ★ 这里写「自家会话」而不是与种类名逐字相同的「自家客服会话」:原型标签就贴在卡名下面,
  //   同一张卡上把同四个字摆两遍,读的人会以为自己看重了(真渲染前就被测试抓出来了)。
  first_party: "自家会话",
};

export function archetypeLabel(code: string): string {
  return ARCHETYPE_LABEL[code] ?? code;
}

/**
 * **内容线**的三个原型(契约 §16.4)。
 *
 * ★★ 它们的出口不是电话,是一篇回答 / 一条私信 —— 所以「这轮 0 张」在这几条线上
 *   **不是失败**。上一版对所有线说同一句「这轮没发现,没扣分,换个词或者换个地方找找」,
 *   于是一个正确工作的内容线看起来像是没找到人,用户把它关掉了(它本来就抓不回"一条线索",
 *   它抓的是"一个现在就该回答的问题")。
 * ★ `search` 目前还没有一张卡用它(微信搜一搜挂的是 `question`),但契约把它写进了这一档 ——
 *   api 哪天把它分出来,这一行**当场就成立**,不用再改一次前端。
 * ★ 取值域与 `ARCHETYPE_LABEL` 一样是**开放**的:这里只判"是不是这三个",
 *   不判"只可能是那八个"。
 */
export const CONTENT_ARCHETYPES: readonly string[] = ["question", "comment", "search"];

export function isContentArchetype(code: string | null | undefined): boolean {
  return !!code && CONTENT_ARCHETYPES.includes(code);
}

/**
 * 1009 评论线改造(契约 §1):**评论原型**的种类。api 以目录的 `archetype === "comment"` 为准;
 * 这张表只在 `archetype` 缺席(老数据 / 只拿到 type 的地方)时兜底。
 */
export const COMMENT_TYPES: readonly string[] = [
  "douyin_comment",
  "xhs_note",
  "kuaishou_comment",
  "bilibili_comment",
  "xigua_comment",
];

export function isCommentArchetype(
  item: { type?: string | null; archetype?: string | null } | null | undefined
): boolean {
  if (!item) return false;
  if (item.archetype) return item.archetype === "comment";
  return !!item.type && COMMENT_TYPES.includes(item.type);
}

/**
 * 卡面要摆的话题词(契约 §5):评论原型且 `topicWords` 非空 ⇒ 它;否则 `null`(调用方回落 keywords 逻辑)。
 * ★ 老后端不发 `topicWords` ⇒ 恒 null ⇒ 卡面逐字不变。
 */
export function cardTopicWords(item: {
  type?: string | null;
  archetype?: string | null;
  topicWords?: string[] | null;
}): string[] | null {
  if (!isCommentArchetype(item)) return null;
  const words = (item.topicWords ?? []).filter((w) => !!w && w.trim().length > 0);
  return words.length > 0 ? words : null;
}

/** 卡面话题词那一组的标题。 */
export const TOPIC_WORDS_LABEL = "话题词";

/**
 * 评论线没配话题词时的提示(1027)。措辞照 api `AcqRunKeywords` 的真实取词顺序写:
 * 话题词 → 在售产品名 → 关键词;都空时 ai 退回行业包信号词 —— 那套词是给公示线写的
 * (「浙江数商 / 拟入选名单 / 公示」),拿去搜视频会搜出酒店、好人榜(1027 测试机实锤)。
 */
export const COMMENT_NO_TOPIC_HINT =
  "没配话题词：会先拿在售产品名去搜，没有产品就用行业默认词，常搜到不相关的视频。建议填 3～5 个话题词。";

/** 抽屉里把原有话题词删光时的提示(1027)。 */
export const COMMENT_TOPIC_CLEARED_HINT =
  "话题词清空后，这条线会改用在售产品名或行业默认词去搜，容易搜到不相关的视频。";

/**
 * 评论线卡片要不要提示「没配话题词」。
 * ★ 只认后端**真发了** `topicWords` 数组且为空的:老后端不发这一格(恒 undefined),
 *   那时提示就是在冤枉一条其实配了词的线。
 */
export function lacksCommentTopicWords(item: {
  type?: string | null;
  archetype?: string | null;
  topicWords?: string[] | null;
}): boolean {
  return (
    isCommentArchetype(item) && Array.isArray(item.topicWords) && cardTopicWords(item) === null
  );
}

/**
 * 话题词上限(1023):与 api `KEYWORD_BUDGET_COMMENT_TOPIC` 同数 —— 每轮采集只拿前这么多个去搜。
 * 此前编辑框不限,用户加到第 6、7 个:保存成功、卡上折进「还有 N 个」、采集也不搜。
 */
export const TOPIC_WORDS_MAX = 5;

/** 内容线目录卡上那一行 KPI 口径(契约 §16.4 逐字)。 */
// 1004 第三轮:原句「不按线索条数考核;0 张不扣分」是内部考核口径,客户看不懂也不该看到 → 改成对客说法。
export const CONTENT_KPI_NOTE =
  "找到的是正在提问的人，去回答、评论就能接住他们；这条线不直接给联系方式";

/** 内容线跑完 0 张时的 toast 标题(契约 §16.4 逐字)。 */
export const CONTENT_ZERO_TOAST = "这条线找到的是可回答的问题；本轮 0 张，不扣分";

/**
 * 落选原因 → 中文面(键与 ai `DiscoverResult.reject_breakdown` 同一套)。
 * 取值域开放:查不到的键归进「其他」,不原样显示英文 code。
 */
export const REJECT_REASON_LABEL: Record<string, string> = {
  peer: "同行 / 服务商发的",
  no_need: "科普资讯、看不出需求",
  off_product: "有需求但和在售产品对不上",
  noise: "闲聊或与行业无关",
  low_intent: "意向太弱",
  not_customer: "命中「不是客户」规则",
  title_excluded: "标题被排除词挡掉",
  off_focus: "不贴本轮选的产品",
  judge_failed: "判读服务没跑通",
  unclassified: "未归类",
};

/** 各主因下给用户的那一句建议。★ 必须是用户能照着做的动作,不是解释我们的内部判据。 */
const REJECT_ADVICE: Record<string, string> = {
  peer: "这个渠道里写这个话题的多是从业者。换成客户遇到问题时会说的词(如「××被驳回怎么办」「××多少钱」),或改用知乎、评论区这类有人提问的渠道。",
  no_need: "多是科普和资讯。换成带「怎么办 / 多少钱 / 求推荐」的词,更容易找到真正在问的人。",
  off_product: "拉回来的需求和你在售的产品对不上 —— 检查这条线的关键词是否对应你在卖的产品。",
  noise: "多是闲聊或与行业无关的内容,关键词可能太泛,换得具体一些。",
  judge_failed: "判读服务这轮没跑通,不是没有内容。稍后再跑一次,不扣分。",
};

/**
 * 0 张时的那句话:拉到了多少、主要卡在哪、下一步怎么办(0923)。
 *
 * ★ 测试机实证:微信搜一搜抓 156 篇、全判 0,旧提示「看了 156 条原始信号,没有一条够格」
 *   被用户读成「拉不到数据」。真实原因是召回里 35/36 是同行软文 —— 这种 0 和「词跟在售产品
 *   对不上」的 0,用户该做的事完全不同,必须分开说。
 * ★ 没有计数(老后端 / 第一方线)返回 null,调用方沿用旧文案。
 */
export function explainZeroRun(
  rawCount: number,
  breakdown: Record<string, number> | null | undefined
): string | null {
  const entries = Object.entries(breakdown ?? {}).filter(([, n]) => n > 0);
  if (rawCount <= 0 || entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  const [topKey, topN] = entries[0];
  const parts = entries
    .slice(0, 3)
    .map(([k, n]) => `${REJECT_REASON_LABEL[k] ?? "其他"} ${n} 条`)
    .join("、");
  const head = `拉到 ${rawCount} 条内容，都没成为商机：${parts}。`;
  // 主因要占到一半以上才给针对性建议;分散的时候给一句通用的,别把次要原因说成主因。
  const advice =
    topN * 2 >= rawCount && REJECT_ADVICE[topKey]
      ? REJECT_ADVICE[topKey]
      : "可以换一组更贴近客户说法的关键词再试。";
  return `${head}${advice}`;
}

/** 0 张且来源什么都没返回时的那句话(0925,funnel.fetched == 0)。 */
export const FUNNEL_NOTHING_FETCHED = "这轮没抓到内容（来源没返回），不扣分";

/**
 * 0 张时按本轮漏斗说的那句话(0925)。
 *
 * ★ 0923 测试机:搜一搜抓 156 篇、判读全 0,用户读成「拉不到数据」。漏斗把两种 0 分开:
 *   - `fetched == 0`:来源真的没返回 → 「这轮没抓到内容」;
 *   - `fetched > 0 && hits == 0`:抓到了、判下来都不是客户 → 说出抓到几篇、几篇是同行,给下一步。
 * ★ 返回 null 的三种情况,调用方回落 `explainZeroRun` / 旧文案(现文案不动):
 *   - 没有漏斗(老后端 / 第一方线);
 *   - 判读服务没跑通(`judge_failed`):那是「没判成」,不是「判了都不是」,有它自己的那句话;
 *   - `hits > 0`(卡出了但没落成,比如剥不出去重键)—— 这时说「0 条是潜在客户」是假话。
 */
export function explainZeroRunByFunnel(
  funnel: AcqDiscoverFunnel | null | undefined,
  breakdown?: Record<string, number> | null
): string | null {
  if (!funnel) return null;
  if ((breakdown?.judge_failed ?? 0) > 0) return null;
  if (funnel.fetched <= 0) return FUNNEL_NOTHING_FETCHED;
  if (funnel.hits > 0) return null;
  const peer =
    funnel.rejectedAsPeer > 0 ? `（其中 ${funnel.rejectedAsPeer} 篇是同行/机构软文）` : "";
  return `抓到 ${funnel.fetched} 篇，判读后 0 条是潜在客户${peer}，不扣分；换个更贴近客户说法的关键词试试`;
}

/**
 * 意向类型 → 中文面(契约 §14.3 逐字)。
 *
 * ★ 它与 `STATE_LABEL`(这条走到哪一步了)、`ACTION_SPEC`(该做什么)回答的是**三件事**:
 *   意向说的是**对方想干什么** —— 同样是「问价」,在知乎上要写回答、在客服会话里要回一句话,
 *   但话术的重心都得压在价钱上。话术按它走(ai 侧 take 的 prompt 吃这一格)。
 * ★ 取值域开放:查不到的原样显示 code(与 source / archetype 同则)——
 *   后端加了新类型,面上当场露出 `XX_NEW` 而不是一个空 pill。
 */
export const INTENT_TYPE_LABEL: Record<string, string> = {
  PURCHASE: "采购",
  PRICE: "问价",
  QUALIFICATION: "资质申报",
  COMPLIANCE: "合规维权",
  EXPANSION: "扩张出海",
  PRODUCT: "问产品",
  OTHER: "其他",
};

/** 筛子里给哪几档、按什么顺序(值域见 `AcqIntentType`)。 */
export const INTENT_TYPE_ORDER: readonly string[] = [
  "PURCHASE",
  "PRICE",
  "QUALIFICATION",
  "COMPLIANCE",
  "EXPANSION",
  "PRODUCT",
  "OTHER",
];

export function intentTypeLabel(code: string): string {
  return INTENT_TYPE_LABEL[code] ?? code;
}

/**
 * 第一方意向的 source 码(契约 §14.1)。
 * ★ 写成常量是因为 console 里有**三处**要按它分支,而它们分的不是"这一种叫什么"
 *   (那种文案归 api 的目录登记表),是**这条线的原文在自家页面上**:
 *   ①「看原文」要说「查看会话」;②主按钮要说「回到会话里回复」;③产物是回复草稿。
 */
export const FIRST_PARTY_SOURCE = "first_party";

/**
 * 主按钮文案:同一个动作码在不同来源上说的不是同一件事(契约 §14.1)。
 *
 * ★★ **`ACTION_SPEC` 不加新动作值**(第一方线仍然用 WECHAT):加一个 `REPLY` 码
 *   会让 ai / api / console 三处取值域一起改,而出口其实一模一样 —— 变的只是措辞。
 * ★ 「回到会话里回复」与「复制话术,去加微信」差的是那个**回**字:对方已经在跟你说话了,
 *   叫人去"加微信"等于让他把一个正在问价的人当陌生人重新认识一遍。
 */
export function actionLabelFor(action: AcqSuggestedAction | string, source: string): string {
  if (source === FIRST_PARTY_SOURCE && action === "WECHAT") return "回到会话里回复";
  return actionSpecFor(action).label;
}

/** 联系方式来源的中文面(契约 §14.4)。取值域开放,查不到原样显示 provider 码。 */
const PROVENANCE_LABEL: Record<string, string> = {
  announcement: "公告原文",
  cs_session: "客服会话",
  zhipu: "网上查到",
  // 1015 企业库:工商登记留存的号码。不写供应商名(同「网上查到」不写智谱)。
  tianyancha: "工商登记",
  qichacha: "工商登记",
  company_db: "工商登记",
  // 1025 启客拓客:用户在拓客工作台里自己解锁的(我方不扣分)。
  qike: "拓客平台",
};

/**
 * 补全价签(1015 企业库):单价写「50 积分」;接了企业库、上下沿不同写「50–120 积分」。
 * 任何一个缺席 ⇒ 退回单价那一档;单价也没有 ⇒「按次计费」,**永不编数**。
 */
export function unlockPriceText(min: number | null, max?: number | null): string {
  if (min == null) return "按次计费";
  if (max != null && max > min) return `${min}–${max} 积分`;
  return `${min} 积分`;
}

/** 企业画像一行字:只拼有值的格子;全空回 `""`(调用方据此整块不渲染)。 */
export function companyProfileText(
  c:
    | {
        legalPerson?: string | null;
        regCapital?: string | null;
        establishDate?: string | null;
        regStatus?: string | null;
        industry?: string | null;
      }
    | null
    | undefined
): string {
  if (!c) return "";
  const parts = [
    c.legalPerson ? `法人 ${c.legalPerson}` : "",
    c.regCapital ? `注册资本 ${c.regCapital}` : "",
    c.establishDate ? `成立 ${c.establishDate}` : "",
    c.regStatus ?? "",
    c.industry ?? "",
  ].filter(Boolean);
  return parts.join(" · ");
}

/**
 * 联系方式旁那一行小字(契约 §14.4)。
 *
 * ★★ **缺席就是「来源未知」**(契约 §14.7):不许回落成「公告原文」——
 *   一个编出来的出处会让销售把一串来路不明的号码当成公告上抄的,打错了还以为是公告错了。
 * ★ 后缀三态,按"这串号码有多可信"给**不同的**一句:
 *   · 明显没把握(<0.95)→ 报百分比,让人自己掂量;
 *   · 把握满格(1,自家会话里对方亲口留的)→ **什么也不加**:再报一次"什么时候核实的"是噪音;
 *   · 中间那档 → 报核实日期(公告是哪天的比"95%"有用得多)。
 */
export function contactProvenanceText(
  p:
    | { provider?: string | null; confidence?: number | null; verifiedAt?: string | null }
    | null
    | undefined
): string {
  const provider = p?.provider?.trim();
  if (!provider) return "来源未知";
  const who = PROVENANCE_LABEL[provider] ?? provider;
  const conf = p?.confidence;
  if (conf != null && conf < 0.95) return `来源：${who} · 置信度 ${Math.round(conf * 100)}%`;
  if (conf != null && conf >= 1) return `来源：${who}`;
  const day = monthDayText(p?.verifiedAt);
  return day ? `来源：${who} · ${day} 核实` : `来源：${who}`;
}

/**
 * 「09-17」—— 卡上那几行要的是**哪天**,不是精确到秒。
 * ★ 认不出格式的回空串(调用方据此整段不渲染),**不回落成原串** ——
 *   一行 `2026-09-17T10:30:00.123456+08:00` 出现在卡上,读的人只会以为页面坏了。
 */
export function monthDayText(ts?: string | null): string {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(ts ?? "");
  return m ? `${m[1]}-${m[2]}` : "";
}

/**
 * 「同行业反馈」一句话(契约 §13.1 学习环 v1)。
 *
 * ★★ **全 0 时回 `null`,面上整行不出现**:「成了 0 · 找错人 0」会被读成
 *   "这条线同行试过了,不行",而事实是**还没人试过**。这两件事在决定要不要花 180 积分
 *   跑一次的时候意思正相反。
 * ★ 只报「成了」和「找错人」两格:前者是这条线值不值,后者是它准不准 ——
 *   把四个数全摆上去就成了一个没人读的小仪表盘(契约 §9③ 不做仪表盘)。
 * ★ `shareBp` 不进面:它是采集份额,是我们内部怎么分配抓取,不是用户该操心的事。
 */
export function peerFeedbackText(
  pf: { won: number; wrongPerson: number } | null | undefined
): string | null {
  if (!pf) return null;
  if (!pf.won && !pf.wrongPerson) return null;
  return `同行业反馈：成了 ${pf.won} · 找错人 ${pf.wrongPerson}`;
}

export function stateLabel(state: AcqState): string {
  return STATE_LABEL[state] ?? state;
}

/** 契约 §4.2 五按钮文案,逐字。 */
export const FEEDBACK_LABEL: Record<AcqFeedbackCode, string> = {
  won: "成了",
  lost: "没成",
  wrong_person: "找错人",
  bad_phone: "电话无效",
  follow_up: "再跟进",
};

/**
 * 反馈 → 状态(契约 §4.2,0917 修订)。
 *
 * ★ 五个**全都**推进状态。上一版只有 `follow_up` 动状态,后果是:用户点了「成了」,
 *   这条却永远停在 READY_TO_CONTACT —— 漏斗推不到 WON,而面上看不出任何异常。
 * ★ 权威在服务端(`POST /feedback` 返回整张更新后的卡),这份表只用于 dev 桩与前端预期,
 *   **不要拿它在前端抢先改 state**。
 */
export const FEEDBACK_NEXT_STATE: Record<AcqFeedbackCode, AcqState> = {
  won: "WON",
  lost: "LOST",
  wrong_person: "LOST",
  bad_phone: "LOST",
  follow_up: "NURTURE",
};

/**
 * ★★ 三种反馈都落 `LOST`,但它们**不是一回事**:
 *   · `lost`         = 谈过了没成 —— 销售结果;
 *   · `wrong_person` = 找错人      ⎫ 采集端的毛病(fit / reach 维度重罚),
 *   · `bad_phone`    = 电话无效    ⎭ 跟销售干得好不好无关。
 * 所以**凡是做漏斗/统计的地方必须同时读 `feedbackCode`,不能只读 `state`** ——
 * 只按 state 数,会把数据质量失败记成销售的败仗,然后照着一份假的转化率去改销售流程。
 */
export const DATA_QUALITY_FEEDBACKS: readonly AcqFeedbackCode[] = ["wrong_person", "bad_phone"];

export function isDataQualityFailure(code: AcqFeedbackCode | null | undefined): boolean {
  return !!code && DATA_QUALITY_FEEDBACKS.includes(code);
}

/**
 * 契约 §10 初版权重。**只在这一处写**(桩数据自洽性由 acqService.test 锁住);
 * 真正的 `priorityScore` 是服务端算好的快照,前端**永不复算**、只用来对表。
 */
export const SCORE_WEIGHTS = { fit: 0.35, intent: 0.3, timing: 0.25, reach: 0.1 } as const;

export function weightedPriority(s: AcqScores): number {
  return Math.ceil(
    s.fit * SCORE_WEIGHTS.fit +
      s.intent * SCORE_WEIGHTS.intent +
      s.timing * SCORE_WEIGHTS.timing +
      s.reach * SCORE_WEIGHTS.reach
  );
}

/**
 * 一个 `suggestedAction` → **一个**主按钮(契约 §4.1 硬要求③:不给动作清单)。
 *
 * ★ 外链三卡在这里从"第四种获客方式"变成"动作的出口"(契约 §8):
 *   该打电话 → 外呼系统;该发邮件 → 邮件系统。没有对应系统的动作(加微信/上门)
 *   出口就是**话术本身**,复制走人 —— 不为了凑齐而假造一个落点。
 */
export interface ActionSpec {
  /** 主按钮文案。 */
  label: string;
  /** 需要联系方式吗?需要而未解锁时,主按钮先变成"解锁"。 */
  needsContact: boolean;
  /** 落点:外部工具页的 tool key(没有就靠复制话术)。 */
  tool?: "waihu" | "email";
  /** 点完把这条推进到哪个状态。 */
  nextState: "FIRST_TOUCH" | "NURTURE";
  /** 点按钮顺带把话术复制走。 */
  copyOpener?: boolean;
  /**
   * 点按钮把**接手产物**(`deliverable.text`)复制走 —— 比 `copyOpener` 晚一版:
   * v2 的话术出处是 AI 现出的产物,不再是卡上那句固定的 `inference.opener`。
   */
  copyDeliverable?: boolean;
  /** 点按钮新窗口打开 `deliverable.targetUrl`(问题页 / 作者主页)。 */
  openTarget?: boolean;
}

export const ACTION_SPEC: Record<AcqSuggestedAction, ActionSpec> = {
  CALL: {
    label: "去外呼系统打这通电话",
    needsContact: true,
    tool: "waihu",
    nextState: "FIRST_TOUCH",
  },
  EMAIL: {
    label: "去邮件系统发这封信",
    needsContact: true,
    tool: "email",
    nextState: "FIRST_TOUCH",
  },
  WECHAT: {
    label: "复制话术,去加微信",
    needsContact: true,
    nextState: "FIRST_TOUCH",
    copyOpener: true,
  },
  VISIT: {
    label: "复制话术,安排上门",
    needsContact: true,
    nextState: "FIRST_TOUCH",
    copyOpener: true,
  },
  WATCH: { label: "先放进观察,不打扰", needsContact: false, nextState: "NURTURE" },
  // ★ 0917 v2 两个新出口(契约 §12.6)。两个都 `needsContact: false` ——
  //   它们是**内容线**的落点:知乎提问者/微博作者本来就拿不到电话,也不需要。
  // ★ 这两个的出口是「复制产物 + 打开那个页面」两件事一起做:
  //   只复制不开页,用户还得自己去找那条问题;只开页不复制,他到了那儿手上是空的。
  ANSWER: {
    label: "去回答这个问题",
    needsContact: false,
    nextState: "FIRST_TOUCH",
    copyDeliverable: true,
    openTarget: true,
  },
  DM: {
    label: "复制话术,去私信",
    needsContact: false,
    nextState: "FIRST_TOUCH",
    copyDeliverable: true,
    openTarget: true,
  },
};

/**
 * 服务端给了一个本仓还不认识的动作时的兜底。
 *
 * ★ 为什么要有它:动作取值域**会长**。0917 实测知乎提问者不可触达(问题详情 16 个字段
 *   零个作者字段)⇒ 知乎从线索源改成**内容线**,那条线的出口是「今天该答的问题」,
 *   不是「今天该打的电话」,迟早要多一个动作码。没有兜底的话,新动作到达时
 *   `ACTION_SPEC[x]` 是 undefined,主按钮**整个不渲染** —— 卡还在、看着正常、
 *   就是点不了,而这种失败没有任何一处会报错。
 * ★ 兜底 `needsContact: false`:不认识的动作不许替用户决定"得先花 50 积分解锁"。
 */
const UNKNOWN_ACTION: ActionSpec = {
  label: "记下来,我去跟",
  needsContact: false,
  nextState: "FIRST_TOUCH",
};

export function actionSpecFor(action: AcqSuggestedAction | string): ActionSpec {
  return ACTION_SPEC[action as AcqSuggestedAction] ?? UNKNOWN_ACTION;
}

/**
 * 发现时间一句话(1004 A4):「事件多久前发生」与「我们哪天发现的」是两个时间,分开写。
 * 只写日期不写「N 天前」—— 两个相对时间并排就又分不清谁是谁了。取不到/解析不了返回 ""。
 */
export function discoveredText(detectedAt: string | null | undefined): string {
  const md = monthDayText(detectedAt);
  return md ? `${md} 发现` : "";
}

/**
 * 事件发生距今几天 —— **日历天**(1004 F23b)。
 * 后端给的 `freshnessDays` 按满 24 小时取整:10-03 晚上的评论在 10-04 中午看是「0 天」,
 * 卡头写「今天发生」,事实栏却写着「2026-10-03 的评论」。有 `happenedAt` 就按日期差现算
 * (同 `daysLeft` 的日历天口径);拿不到才退回后端那一格;都没有返回 null(只写发现时间)。
 */
export function happenedDaysAgo(
  card: { happenedAt?: string | null; freshnessDays?: number | null },
  today: Date = new Date()
): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(card.happenedAt ?? "");
  if (m) {
    const ev = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.max(0, Math.round((now - ev) / 86_400_000));
  }
  return card.freshnessDays ?? null;
}

/** 新鲜度一句话。契约 §10:吃 `happenedAt` 不吃 `detectedAt`。 */
export function freshnessText(days: number | null | undefined): string {
  if (days == null) return "";
  if (days <= 0) return "今天发生";
  if (days === 1) return "昨天发生";
  return `${days} 天前发生`;
}

/**
 * 一条画像是谁提的(契约 §16.6)。
 *
 * ★★ **缺席返回 null ⇒ 小标整个不出现**,不回落成「AI 推的」:
 *   后端(`source_channel`)还没跟上的那一版里,每一条都会被标成"AI 推的" ——
 *   而用户自己刚加的那条也在里面,他会认定这一页在胡说,然后不再相信整页的来源标记。
 * ★ 说「你加的」不说「手动」:这一页上和他对话的是"你",不是一份日志。
 */
export function icpSourceLabel(source: "ai" | "user" | null | undefined): string | null {
  if (source === "ai") return "AI 推的";
  if (source === "user") return "你加的";
  return null;
}

/**
 * 今日商机页副标题(1004 F23b)。原文「今天给你挑了 12 条」,而其中 7 条是 09-24 发现的旧单。
 * 按 `detectedAt` 数一下今天新找到的;数不全(分页、列表没拉完)就不报「今天新」这个数,只说共几条。
 */
export function todayPickedText(
  total: number,
  cards: { detectedAt?: string | null }[] | null,
  today: Date = new Date()
): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const ymd = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  if (cards && cards.length === total) {
    const fresh = cards.filter((c) => (c.detectedAt ?? "").startsWith(ymd)).length;
    if (fresh === total) return `今天新找到 ${total} 条，按优先级排好了`;
    if (fresh === 0) return `今天没有新找到的，之前找到的还有 ${total} 条待跟进，按优先级排好了`;
    return `待跟进 ${total} 条（今天新找到 ${fresh} 条），按优先级排好了`;
  }
  return `待跟进 ${total} 条，按优先级排好了`;
}

/**
 * 「为什么推这条线」一句话(1004 F23b)。ai 的理由按契约写成「场景「X」(K22)」,模型常在后面
 * 再贴几个字;剥掉 K 编号后读成「场景「刷到内容在评论区问价求链接」评论区问价」,不通。
 * 改成「对应你资料里的场景「X」」,后缀另起「:…」。不是这个句式的原样返回(调用方已剥内部标记)。
 */
export function discoveryWhyText(why: string): string {
  const t = (why ?? "").trim();
  const m = /^场景「([^」]+)」[\s,,::;;。]*([\s\S]*)$/.exec(t);
  if (!m) return t;
  const rest = m[2].trim().replace(/[。;;,,]+$/, "");
  return `对应你资料里的场景「${m[1]}」` + (rest ? `：${rest}` : "");
}

/** 目录卡上「找回来的人多半想做什么」那一行的前缀(原「多为：」读不通)。 */
export const INTENT_HINT_PREFIX = "找回来的人多半想：";

/**
 * 这条能不能「补全联系方式」(单条按钮与线索库批量勾选共用一个判据,1015)。三个条件缺一不可:
 *   · 没补过(`!unlocked`)——补过了再点是白花钱的入口;
 *   · 手上确实没号码(`!contact.phone`)——公告里自带联系人的那种卡不该再收一笔;
 *   · 主体是**公司**(`subjectType === "company"`)——知乎提问者、抖音账号本来就没有"官方电话"。
 */
export function canEnrichCard(card: {
  unlocked: boolean;
  subjectType: string;
  contact?: { phone?: string | null } | null;
}): boolean {
  return !card.unlocked && !card.contact?.phone && card.subjectType === "company";
}
